/**
 * Messaging Automation Settings Panel
 *
 * Renders inside Settings → Communication Settings tab.
 * Role-gated: only super_admin / owner / admin can edit; sales_manager can view.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, MessageSquare, Send, ShieldCheck, Eye } from "lucide-react";
import {
  useMessagingAutomation,
  canEditMessagingSettings,
  type MessagingMode,
  type FollowUpSendMode,
} from "@/data/MessagingAutomationContext";
import { useRole } from "@/data/RoleContext";

const MODE_INFO: Record<MessagingMode, { label: string; description: string; badge: string }> = {
  suggestive: {
    label: "Suggestive Mode",
    description: "Messages are generated and presented for review. No outbound messages are sent without explicit user action.",
    badge: "Beta Default",
  },
  automated: {
    label: "Automated Mode",
    description: "Outbound messages are sent automatically based on configured sequences and timing rules.",
    badge: "Advanced",
  },
};

export function MessagingAutomationSettings() {
  const { settings, updateSettings, updateSourceOverride, knownSources } = useMessagingAutomation();
  const { role } = useRole();
  const canEdit = canEditMessagingSettings(role);

  return (
    <div className="space-y-6">
      {/* Global Messaging Mode */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Messaging Mode</CardTitle>
            <Badge variant="outline" className="text-[10px] ml-auto">
              {MODE_INFO[settings.messagingMode].badge}
            </Badge>
          </div>
          <CardDescription className="text-xs">
            Controls how outbound conversation messages are handled across all lead sources.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ModeSelector
            value={settings.messagingMode}
            onChange={(v) => canEdit && updateSettings({ messagingMode: v })}
            disabled={!canEdit}
            type="messaging"
          />
          {settings.messagingMode === "automated" && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <p className="text-xs text-warning">
                Automated Mode sends outbound communication without manual review. Use only after workflows are validated.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Follow-Up Send Mode */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Follow-Up Send Mode</CardTitle>
          </div>
          <CardDescription className="text-xs">
            Controls how disposition-based follow-up messages and thank-you messages are sent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ModeSelector
            value={settings.followUpSendMode}
            onChange={(v) => canEdit && updateSettings({ followUpSendMode: v })}
            disabled={!canEdit}
            type="followup"
          />
          {settings.followUpSendMode === "automated" && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <p className="text-xs text-warning">
                Automated follow-ups will send messages without review. Ensure templates are validated before enabling.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Require Review Before Send */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Require Review Before Send</CardTitle>
          </div>
          <CardDescription className="text-xs">
            When enabled, all outbound messages require explicit human review regardless of mode settings. This is the recommended beta setting.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Review Required</Label>
            <Switch
              checked={settings.requireReviewBeforeSend}
              onCheckedChange={(v) => canEdit && updateSettings({ requireReviewBeforeSend: v })}
              disabled={!canEdit}
            />
          </div>
          {!settings.requireReviewBeforeSend && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 mt-3">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <p className="text-xs text-warning">
                Disabling review allows messages to be sent without manual approval when in Automated Mode.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-Source Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Source-Level Behavior</CardTitle>
          </div>
          <CardDescription className="text-xs">
            Override messaging behavior for individual lead sources. Defaults to the global setting when not overridden.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {knownSources.map((source) => {
              const override = settings.sourceOverrides[source];
              return (
                <div key={source} className="flex flex-wrap items-center gap-3 rounded-md border p-3">
                  <span className="text-xs font-medium min-w-[100px]">{source}</span>
                  <div className="flex items-center gap-2 flex-1">
                    <Label className="text-[10px] text-muted-foreground shrink-0">Messaging</Label>
                    <Select
                      value={override?.messagingMode ?? settings.messagingMode}
                      onValueChange={(v) => canEdit && updateSourceOverride(source, { messagingMode: v as MessagingMode })}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className="h-7 text-[11px] w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="suggestive">Suggestive</SelectItem>
                        <SelectItem value="automated">Automated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 flex-1">
                    <Label className="text-[10px] text-muted-foreground shrink-0">Follow-Up</Label>
                    <Select
                      value={override?.followUpSendMode ?? settings.followUpSendMode}
                      onValueChange={(v) => canEdit && updateSourceOverride(source, { followUpSendMode: v as FollowUpSendMode })}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className="h-7 text-[11px] w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="suggestive">Suggestive</SelectItem>
                        <SelectItem value="automated">Automated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Role Access Info */}
      {!canEdit && (
        <div className="text-xs text-muted-foreground text-center py-2">
          You have view-only access to messaging automation settings. Contact an administrator to make changes.
        </div>
      )}
    </div>
  );
}

function ModeSelector({
  value,
  onChange,
  disabled,
  type,
}: {
  value: MessagingMode;
  onChange: (v: MessagingMode) => void;
  disabled: boolean;
  type: "messaging" | "followup";
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {(["suggestive", "automated"] as MessagingMode[]).map((mode) => {
        const info = MODE_INFO[mode];
        const isActive = value === mode;
        return (
          <button
            key={mode}
            disabled={disabled}
            onClick={() => onChange(mode)}
            className={`rounded-md border p-3 text-left transition-colors ${
              isActive
                ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                : "border-border hover:border-primary/30"
            } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-xs font-medium">{info.label}</span>
              {mode === "suggestive" && (
                <Badge variant="secondary" className="text-[9px] h-4">Recommended</Badge>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground leading-tight">{info.description}</p>
          </button>
        );
      })}
    </div>
  );
}
